/**
 * Student edit form.
 * Loads full state, hydrates the form, then PATCHes registration + matching in one submit.
 */
import * as React from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  useGetStudentRemoteFullState,
  useGetParentRemoteFullState,
  useUpdateStudentRemoteProfile,
} from '@/hooks/useUsers';
import { useAuthStore } from '@/stores/authStore';
import { CollapseReplaceForm } from '@/components/common/CollapseReplaceForm';
import { usePartnersOptions } from '@/hooks/usePartners';
import { getLevels } from '@/services/levels.service';
import { useProgramsForMode } from '@/hooks/useProgramsForMode';
import { studentRemoteEditValidationSchema } from '@/utils/validation-schemas-utils';
import {
  areContactsIdentical,
  isPrimaryContactDirty,
  isPrimaryContactFormValid,
  isPrimaryContactSubmitBlocked,
  readPhoneSetupLink,
  toPrimaryContactMode,
  useDefaultPhoneCountry,
} from '@/utils/user-contact-utils';
import { ContactChangeModal } from '@/components/common/modals/ContactChangeModal';
import { useContactChangeOnSubmit } from '@/hooks/useContactChangeOnSubmit';
import { useFormValidation } from '@/hooks/useFormValidation';
import { useUnitGetNotFound } from '@/hooks/useUnitGetNotFound';
import { resolveContactCheckType, useContactExistsCheck } from '@/hooks/useContactExistsCheck';
import type { ContactKind } from '@/types/user.types';
import { toast } from '@/components/ui/toast';
import { buildPendingDocumentFilePatch } from '@/utils/pending-document-link-utils';
import { hasPendingUploadFiles, uploadPendingFiles } from '@/utils/pending-upload-utils';
import { getErrorMessage } from '@/utils/errors-utils';
import { preserveClearedMeetingSessionId } from '@/utils/meeting-selector-utils';
import {
  REMOTE_SUBJECT_OPTIONS,
  STUDENT_FORM_QUERY_KEYS,
} from '@/components/students/student-configs';
import {
  isAdultFromDob,
  hydrateFullStateStudentForm,
  buildLevelOptions,
  buildNamedOptions,
  studentHasSportProgram,
  studentValidationPayload,
  applyNestedPath,
  applyStudentFormField,
  buildUpdateStudentDto,
  defaultUpload,
  mergePendingUploads,
  isStudentPreRegistered,
  isWelcomeParticipationLocked,
  useStudentParentContactPrefill,
  useStudentParentAddressPrefill,
  useSportClubOptions,
} from './student-form-utils';
import { StudentRegistrationSection } from './student-form-builder/StudentRegistrationSection';
import { StudentMatchingSection } from './student-form-builder/StudentMatchingSection';
import { StudentWelcomeMeetingSection } from './student-form-builder/StudentWelcomeMeetingSection';
import { StudentDocumentsSection } from './student-form-builder/StudentDocumentsSection';

/** Props for {@link EditStudentView}. */
interface EditStudentViewProps {
  userId: string;
  stepKey?: string;
  onClose: () => void;
  onSuccess?: () => void;
}

/** Student edit form. */
export function EditStudentView({
  userId,
  stepKey: _stepKey,
  onClose,
  onSuccess,
}: Readonly<EditStudentViewProps>) {
  const { user } = useAuthStore();
  const companyId = user?.companyId ?? '';
  const defaultCountry = useDefaultPhoneCountry();
  const validation = useFormValidation(studentRemoteEditValidationSchema);

  const {
    data: fullState,
    isLoading: isLoadingFullState,
    isError: isFullStateError,
    error: fullStateError,
  } = useGetStudentRemoteFullState(userId, companyId, { enabled: !!userId && !!companyId });
  useUnitGetNotFound({
    isError: isFullStateError,
    error: fullStateError,
    catalogKey: 'studentNotFound',
    onClose,
    entityId: userId,
  });

  const record = fullState?.record;
  const isPreRegistered = isStudentPreRegistered('edit', record);

  const { data: levels = [] } = useQuery({
    queryKey: STUDENT_FORM_QUERY_KEYS.levels,
    queryFn: async () => (await getLevels({ pageSize: 10 })).data,
  });

  const { data: programsResponse } = useProgramsForMode({ statuses: ['active'] });
  const catalogPrograms = programsResponse?.data;
  const programs = catalogPrograms ?? [];

  const { data: partners = [], error: partnersError } = usePartnersOptions();
  const { data: sportClubs = [], error: sportClubsError } = useSportClubOptions();

  const levelOptions = React.useMemo(() => buildLevelOptions(levels), [levels]);
  const partnerOptions = React.useMemo(() => buildNamedOptions(partners), [partners]);
  const sportClubOptions = React.useMemo(() => buildNamedOptions(sportClubs), [sportClubs]);

  const [formData, setFormData] = React.useState<Record<string, any>>({});
  const [isHydrated, setIsHydrated] = React.useState(false);
  const [contactType, setContactType] = React.useState<ContactKind>('unknown');
  const [secondaryContact, setSecondaryContact] = React.useState('');
  const [pendingFiles, setPendingFiles] = React.useState<Record<string, File | null>>({});
  const [isUploading, setIsUploading] = React.useState(false);

  const parentUserId = formData.parentUserId || record?.linkedUserId || '';
  const { data: parentFullState } = useGetParentRemoteFullState(parentUserId, companyId, {
    enabled: !!parentUserId && !!companyId,
  });

  const isAdult = React.useMemo(() => isAdultFromDob(formData.dateOfBirth), [formData.dateOfBirth]);
  const contactKind = toPrimaryContactMode(contactType);
  const isPrimaryDirty =
    isAdult &&
    isPrimaryContactDirty({
      mode: contactKind,
      currentValue: formData.contact || '',
      storedEmail: record?.user?.email,
      storedPhone: record?.user?.phone,
      serverKind: record?.user?.primaryContactKind,
      defaultCountry,
    });

  /** Close the edit surface after a credential modal or a clean PATCH. */
  function handleContactChangeFinished(): void {
    onSuccess?.();
  }

  const contactChange = useContactChangeOnSubmit({
    isDirty: isPrimaryDirty,
    kind: contactKind,
    ownProfile: false,
    closeForm: handleContactChangeFinished,
  });
  const { isChecking: isPrimaryChecking, exists: contactExists } = useContactExistsCheck({
    value: formData.contact || '',
    type: resolveContactCheckType(contactType),
    enabled: isAdult && contactType !== 'unknown' && (formData.contact || '').trim().length > 0,
    excludeUserId: userId,
    skipHydratedValue: true,
  });
  const hasSportProgram = studentHasSportProgram(programs, formData.programs);
  const formDataRef = React.useRef(formData);
  formDataRef.current = formData;

  /** Hydrate form from full-state once it arrives. Keep the spinner until then. */
  React.useEffect(() => {
    if (!fullState?.record) {
      setIsHydrated(false);
      return;
    }
    const next = preserveClearedMeetingSessionId(
      formDataRef.current,
      hydrateFullStateStudentForm(fullState.record, defaultCountry, catalogPrograms),
    );
    setFormData(next);
    setContactType((next.contactType as ContactKind) ?? 'unknown');
    setSecondaryContact(next.secondaryContact ?? '');
    validation.validateInitialFormData(
      studentValidationPayload(next, isAdult, next.contactType, next.secondaryContact),
    );
    setIsHydrated(true);
  }, [fullState, defaultCountry, catalogPrograms]);

  useStudentParentContactPrefill({
    isAdult,
    parentUserId,
    parentFullState,
    defaultCountry,
    enabled: isHydrated,
    setFormData,
    setContactType,
    setSecondaryContact,
  });

  useStudentParentAddressPrefill({
    livingWithParents: formData.livingWithParents,
    parentUserId,
    parentFullState,
    setFormData,
  });

  const updateMutation = useUpdateStudentRemoteProfile();

  /** Set one top-level field and re-validate with adult / contact-type context. */
  const handleChange = (key: string, value: any) => {
    setFormData((prev) => applyStudentFormField(prev, key, value));
    validation.validateField(
      key,
      value,
      studentValidationPayload(
        { ...formData, [key]: value },
        isAdult,
        formData.contactType,
        secondaryContact,
      ),
    );
    validation.markAsTouched(key);
    validation.markAsDirty(key);
  };

  /** Mark a field touched so its error can show. */
  const handleBlur = (field: string) => {
    validation.markAsTouched(field);
  };

  /** Patch a dotted path (used by documents nested objects). */
  const handleNestedChange = (path: string, value: any) => {
    setFormData((prev) => applyNestedPath(prev, path, value));
  };

  const contactsAreIdentical =
    isAdult && areContactsIdentical(formData.contact || '', secondaryContact || '');

  /** Store the file for submit upload and write a pending link on the school-report fields. */
  const handleFileChange = (key: string, file: File | null) => {
    setPendingFiles((prev) => ({ ...prev, [key]: file }));
    const patch = buildPendingDocumentFilePatch(key, file);
    if (!patch) return;
    setFormData((prev) => {
      let next = prev;
      Object.entries(patch).forEach(([path, value]) => {
        next = applyNestedPath(next, path, value);
      });
      return next;
    });
  };

  /** Upload pending files, then PATCH registration + matching + documents (welcome participation may be locked). */
  const handleSubmit = async () => {
    if (
      isAdult &&
      isPrimaryContactSubmitBlocked(contactExists, isPrimaryChecking, contactsAreIdentical)
    )
      return;
    const isValid = validation.validateForm(
      studentValidationPayload(formData, isAdult, formData.contactType, secondaryContact),
    );
    if (!isValid) return;

    if (hasPendingUploadFiles(pendingFiles)) {
      setIsUploading(true);
    }
    let uploadedUrls: Record<string, string> = {};
    try {
      uploadedUrls = await uploadPendingFiles(pendingFiles, (file) =>
        defaultUpload(file, companyId, userId),
      );
    } catch (err) {
      toast.error(`Erreur lors de l'upload : ${getErrorMessage(err)}`);
      setIsUploading(false);
      return;
    }

    const enrichedData = mergePendingUploads(formData, uploadedUrls);

    contactChange.requestSubmit(() => {
      updateMutation.mutate(
        {
          userId,
          dto: buildUpdateStudentDto(
            enrichedData,
            fullState,
            isPreRegistered,
            isAdult,
            defaultCountry,
          ),
          companyId,
        },
        {
          onSuccess: (data) => {
            if (contactChange.isIntercepting) {
              contactChange.onMutationSettled({ ok: true, link: readPhoneSetupLink(data) });
              return;
            }
            toast.success('Dossier mis à jour avec succès');
            handleContactChangeFinished();
          },
          onError: (err: unknown) => {
            toast.error(getErrorMessage(err));
            contactChange.onMutationSettled({ ok: false });
          },
        },
      );
    });
    setIsUploading(false);
  };

  /** True until GET student hydrates the form. */
  const isFormLoading = isLoadingFullState || !record || !isHydrated;

  function renderFormSections(): React.ReactNode {
    if (!record || !isHydrated) {
      return null;
    }
    return (
      <>
        <StudentRegistrationSection
          formData={formData}
          onChange={handleChange}
          onBlur={handleBlur}
          validation={validation}
          levelOptions={levelOptions}
          levels={levels}
          contactType={contactType}
          setContactType={setContactType}
          secondaryContact={secondaryContact}
          setSecondaryContact={setSecondaryContact}
          isAdult={isAdult}
          mode="edit"
          defaultCountry={defaultCountry}
          primaryContactExists={contactExists}
          contactsAreIdentical={contactsAreIdentical}
          userId={userId}
          emailVerified={record.user?.emailVerified}
          serverEmail={record.user?.email}
          serverPhone={record.user?.phone}
          serverPrimaryKind={record.user?.primaryContactKind}
        />
        <StudentMatchingSection
          formData={formData}
          onChange={handleChange}
          programs={programs}
          subjectOptions={REMOTE_SUBJECT_OPTIONS}
          partnerOptions={partnerOptions}
          sportClubOptions={sportClubOptions}
          partnersError={partnersError ? getErrorMessage(partnersError) : undefined}
          sportClubsError={sportClubsError ? getErrorMessage(sportClubsError) : undefined}
          hasSportProgram={hasSportProgram}
        />
        <StudentWelcomeMeetingSection
          formData={formData}
          onChange={handleChange}
          userId={userId}
          companyId={companyId}
          mode="edit"
          user={user}
          participationLocked={isWelcomeParticipationLocked('edit', fullState, record)}
        />
        <StudentDocumentsSection
          formData={formData}
          onChange={handleNestedChange}
          studentUserId={userId}
          companyId={companyId}
          onFileSelect={handleFileChange}
        />
      </>
    );
  }

  return (
    <>
      <CollapseReplaceForm
        formType="edit"
        title="Modifier l'élève"
        subtitle="Modifiez les informations de l'élève"
        onClose={onClose}
        onSubmit={handleSubmit}
        submitLabel="Enregistrer"
        isSubmitting={isUploading || updateMutation.isPending || contactChange.modal.open}
        isValid={
          !isAdult ||
          isPrimaryContactFormValid({
            primaryExists: contactExists,
            isPrimaryChecking,
            identical: contactsAreIdentical,
          })
        }
        isLoading={isFormLoading}
      >
        {renderFormSections()}
      </CollapseReplaceForm>
      <ContactChangeModal
        open={contactChange.modal.open}
        kind={contactChange.modal.kind}
        ownProfile={contactChange.modal.ownProfile}
        phase={contactChange.modal.phase}
        phoneSetupLink={contactChange.modal.phoneSetupLink}
        onClose={contactChange.cancel}
        onConfirm={contactChange.confirm}
        onCopyLink={contactChange.onCopyLink}
      />
    </>
  );
}
